/**
 * styles.css — Halleck Vineyard tenant-scoped CSS.
 *
 * Halleck uses the shared rich-card structure (src/styles/cards.css) themed by
 * the --ws-rich-* defaults (variables.css :host) — the house MAGENTA scaffold.
 * This file layers Halleck's real, live-verified overrides on top: a near-white
 * #FAFAFC header, COPPER #A66836 accents, a 240px gold-glow wine card with a big
 * image, a white copper membership card, and copper-themed event / reviews.
 *
 * Ported from the pre-split feature/widget-halleck-vineyard-variant branch
 * (variables.css :host(.ws-tenant-halleckvineyard) block) onto the new
 * architecture; class names are the post-rename ws-rich-* shared names.
 *
 * Events: the Halleck Event / EventDetails components emit the live white-card +
 * decorative-detail DOM (rewritten from Goose's structure); these rules theme
 * them with Halleck copper — full live parity, including the eventdetail.png top.
 */

/* ── Theme + chrome vars ─────────────────────────────────────────────── */
:host(.ws-tenant-halleckvineyard) {
  --ws-card-accent: var(--ws-rich-accent);
  --ws-card-title-font: "Open Sans", sans-serif;
  --ws-mic-color: #574d39;
  --ws-mic-bg: #d4d4d4;
  --ws-scrollbar-icon-color: #574d39;
  --ws-header-action-color: #574d39;
  --ws-header-logo-color: var(--ws-rich-accent);
  /* Live user bubble is taupe (old index.css .user-message), not --ws-primary. */
  --ws-user-bubble-bg: #a29b8d;
  --ws-avatar-size: 26px;
}

/* Header is near-white #FAFAFC (live), not the magenta --ws-primary base header. */
:host(.ws-tenant-halleckvineyard) .ws-header {
  background: #fafafc;
}
/* Halleck's logo is a wide monogram + wordmark lockup. The base 32px height +
   150px max-width clamps it too small, so size it up here (scoped to Halleck;
   other wineries keep the base 32px / 150px). Overrides the img directly so the
   inline-defaulted --ws-header-logo-height can't clobber it. */
:host(.ws-tenant-halleckvineyard) .ws-header-logo img {
  height: 50px;
  max-width: 220px;
}
/* Banner shows full image height. Base .ws-banner-img crops to a 110px
   object-fit:cover strip — let Halleck size to natural height at full width. */
:host(.ws-tenant-halleckvineyard) .ws-banner-img {
  height: auto;
}
/* Send button glyph is brown #574D39, no fill (old #msg-btn). */
:host(.ws-tenant-halleckvineyard) .ws-btn-send {
  color: #574d39;
}

/* Feature-card CTAs (add-to-cart / Join / Reserve) are COPPER #A66836 (live),
   not the magenta scaffold. Title-case on one line (no uppercase, no wrap). */
:host(.ws-tenant-halleckvineyard) .ws-card-cta,
:host(.ws-tenant-halleckvineyard) .ws-rich-cta {
  background: #a66836;
  text-transform: none;
  white-space: nowrap;
}
:host(.ws-tenant-halleckvineyard) .ws-card-cta:hover,
:host(.ws-tenant-halleckvineyard) .ws-rich-cta:hover {
  background: #92652f;
}

/* Welcome prompt chips — old .option-button: white pill, brown text+border,
   rounded 8px, tan hover, copper selected.
   Chip layout (indent + shrink-to-text) is shared in ChatPanel.css .ws-prompts. */
:host(.ws-tenant-halleckvineyard) .ws-prompt-chip {
  background: #ffffff;
  color: #574d39;
  border: 1px solid #574d39;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 15px;
  font-weight: 600;
}
:host(.ws-tenant-halleckvineyard) .ws-prompt-chip:hover {
  background: #f4e3c1;
  color: #574d39;
  border-color: #574d39;
}
:host(.ws-tenant-halleckvineyard) .ws-prompt-chip .ws-prompt-icon {
  color: inherit;
}
:host(.ws-tenant-halleckvineyard) .ws-prompt-chip.is-selected {
  background: #a66836 !important;
  border-color: #a66836 !important;
  color: #ffffff !important;
  font-weight: 600;
}
:host(.ws-tenant-halleckvineyard) .ws-prompt-chip.is-selected .ws-prompt-icon {
  color: inherit;
}

/* ── Wine listing card — live createWineListing (verified vs live) ──────
   240px card with a gold-glow shadow, big natural-height image, a full-width
   copper "Tasting notes" pill, copper title, and a ONE-ROW footer (copper price
   block left, copper "Buy Now" right). */
:host(.ws-tenant-halleckvineyard) .ws-rich-winecard {
  width: 240px; /* shared scaffold defaults to 200px */
  border: 1px solid #cbb389;
  border-radius: 6px;
  box-shadow: 0 2px 12px 0 rgba(100, 70, 25, 0.28);
}
:host(.ws-tenant-halleckvineyard) .ws-rich-winetitle {
  color: #a66836;
  font-size: 16px;
  font-weight: 600;
}
/* Cover image LARGE — natural height, near edge-to-edge (scaffold caps at 170px
   contain). */

:host(.ws-tenant-halleckvineyard) .ws-halleck-winecard {
  width: 240px;
  flex: 0 0 auto;
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  box-shadow: var(--ws-shadow);
}

:host(.ws-tenant-halleckvineyard) .ws-halleck-head {
  position: relative;
  padding: 12px 12px 0;
}

:host(.ws-tenant-halleckvineyard)
  .ws-halleck-winecard
  .ws-card-head
  .ws-card-img {
  width: 100%;
  max-width: none;
  height: 170px;
  aspect-ratio: auto;
  object-fit: contain;
  margin: 0;
  display: block;
  background: transparent;
}

/* Same badge as goosecross .ws-goose-soldout — bottom-left on the image. */
:host(.ws-tenant-halleckvineyard) .ws-halleck-soldout {
  position: absolute;
  left: 12px;
  bottom: 8px;
  background: #7b0323;
  color: #ffffff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  padding: 4px 10px;
}

:host(.ws-tenant-halleckvineyard) .ws-halleck-tasting {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 8px 12px;
  margin: auto;
  margin-bottom: 10px;
  border: 1px solid var(--ws-primary);
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 4px 9.7px 0 rgba(110, 24, 17, 0.25);
  color: var(--ws-primary);
  font-size: 15px;
  line-height: 1;
  cursor: pointer;
}
:host(.ws-tenant-halleckvineyard) .ws-halleck-tasting:hover {
  border-color: #a66836;
  color: #92652f;
}

:host(.ws-tenant-halleckvineyard) .ws-halleck-winetitle {
  font-size: 16px;
  font-weight: 600;
  color: #a66836;
  margin: 0;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

:host(.ws-tenant-halleckvineyard) .ws-halleck-winecard-meta,
:host(.ws-tenant-halleckvineyard) .ws-halleck-winecard-desc {
  margin: 0;
  font-family: var(--ws-card-desc-font);
  font-size: 15px;
  font-weight: 400;
  line-height: 120%;
  color: var(--ws-text);
}

/* Horizontal bottom row: price block left, BUY NOW right. */
:host(.ws-tenant-halleckvineyard) .ws-halleck-bottom {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 8px;
  margin-top: 4px;
}

:host(.ws-tenant-halleckvineyard) .ws-halleck-priceblock {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

:host(.ws-tenant-halleckvineyard) .ws-halleck-priceblock .ws-card-price {
  margin: 6px 0 0;
  font-size: 16px;
  font-weight: 600;
  color: #a66836;
  line-height: 1.1;
}

:host(.ws-tenant-halleckvineyard) .ws-halleck-priceblock .ws-card-unit {
  font-size: 11px;
  color: #574d39;
  margin: 2px 0 0;
}

:host(.ws-tenant-halleckvineyard) .ws-halleck-cta {
  flex: 0 0 auto;
  display: inline-block;
  padding: 10px 14px;
  background: #a66836;
  color: #ffffff;
  border-radius: 4px;
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

:host(.ws-tenant-halleckvineyard) .ws-halleck-cta:hover {
  background: #92652f;
}

:host(.ws-tenant-halleckvineyard) .ws-rich-footer {
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 5px;
}
:host(.ws-tenant-halleckvineyard) .ws-rich-pricerow {
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
  width: 100%;
}
:host(.ws-tenant-halleckvineyard) .ws-rich-pricerow .ws-card-price {
  color: #a66836;
}
:host(.ws-tenant-halleckvineyard) .ws-rich-pricerow .ws-card-unit {
  color: #000000;
  font-size: 12px; /* scaffold sets 11px */
}
:host(.ws-tenant-halleckvineyard) .ws-rich-cta {
  width: 100%;
  padding: 9px;
  background: #a66836;
  border: 1px solid #a66836;
  border-radius: 2px;
  font-size: 14px;
  font-weight: 600;
}
:host(.ws-tenant-halleckvineyard) .ws-rich-cta:hover {
  background: #92652f;
}

/* ── Membership card — live createWineMembership (.card-box): a WHITE card with
   COPPER accents, NOT the shared magenta/burgundy design.
     - white card (subtle tan border) + gold-glow shadow
     - logo head on white, natural aspect (not square-letterboxed)
     - title: copper #A66836, LEFT-aligned
     - NO pink inner panel — price + bullets sit on white, dark #333 text
     - "Learn more": copper pill, no shadow
     - footer: copper price / dark unit LEFT, copper "Join Now" RIGHT (radius 2px) */
:host(.ws-tenant-halleckvineyard) .ws-rich-membership {
  background: #ffffff;
  border: 1px solid #cbb389;
  border-radius: 6px;
  box-shadow: 0 2px 12px 0 rgba(100, 70, 25, 0.28);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
:host(.ws-tenant-halleckvineyard) .ws-rich-membership .ws-card-head {
  background: #ffffff;
  padding: 6px;
}
/* Natural aspect (base forces square, letterboxing the wide house logo). */
:host(.ws-tenant-halleckvineyard) .ws-rich-membership .ws-card-img {
  aspect-ratio: auto;
  height: auto;
}
:host(.ws-tenant-halleckvineyard) .ws-card-img.ws-rich-mem-logo {
  object-fit: contain;
  padding: 14px 19px;
  background: #ffffff;
}
/* The header-logo fallback (250x108) was upscaling to fill the card width
   (~126px tall), dominating the card. Cap it to a compact, centered brand mark.
   More specific than the height:auto rule above so it wins. */
:host(.ws-tenant-halleckvineyard)
  .ws-rich-membership
  .ws-card-img.ws-rich-mem-logo {
  height: 150px;
  width: auto;
  max-width: 100%;
  object-fit: contain;
  margin: 0 auto;
  display: block;
  padding: 10px 0 6px;
}
:host(.ws-tenant-halleckvineyard) .ws-rich-membership .ws-card-title {
  color: #a66836;
  text-align: left;
  font-size: 16px;
  font-weight: 400;
  padding: 4px 10px;
  margin: 0;
}
:host(.ws-tenant-halleckvineyard) .ws-rich-inner {
  background: transparent;
  padding: 0 10px 10px;
  margin: 0;
  border-radius: 0;
}
:host(.ws-tenant-halleckvineyard) .ws-rich-inner .ws-card-price {
  color: #333333;
  font-size: 14px;
  font-weight: 400;
  margin: 0 0 6px;
}
:host(.ws-tenant-halleckvineyard) .ws-rich-bullets li {
  color: #333333;
  font-size: 14px;
  font-weight: 400;
}
:host(.ws-tenant-halleckvineyard) .ws-rich-check {
  color: #333333;
}
:host(.ws-tenant-halleckvineyard) .ws-card-learnmore {
  border-color: #a66836;
  color: #a66836;
  box-shadow: none;
}

/* Tasting-experience card (TastingCard) — bring it to Halleck copper like the
   wine/membership cards (the shared scaffold leaves title/price on the magenta
   --ws-card-accent when the tenant has no card_accent_color). Taller 4:3 image
   so the bottles aren't cropped short; keep the description line-height tight. */
:host(.ws-tenant-halleckvineyard) .ws-tasting-card .ws-card-title,
:host(.ws-tenant-halleckvineyard) .ws-tasting-card .ws-card-price {
  color: #a66836;
}
:host(.ws-tenant-halleckvineyard) .ws-tasting-card .ws-card-img {
  aspect-ratio: 4 / 3;
}
:host(.ws-tenant-halleckvineyard) .ws-tasting-card .ws-card-desc-full {
  line-height: 1.4;
}
:host(.ws-tenant-halleckvineyard) .ws-rich-membership-footer {
  align-items: flex-end;
  margin-top: 16px;
}
:host(.ws-tenant-halleckvineyard) .ws-rich-membership-footer .ws-card-cta {
  border-radius: 2px;
  padding: 9px 14px;
}
:host(.ws-tenant-halleckvineyard) .ws-rich-mem-price {
  display: flex;
  flex-direction: column;
}
:host(.ws-tenant-halleckvineyard) .ws-rich-mem-price .ws-card-price-amount {
  font-size: 16px;
  font-weight: 700;
  color: #a66836;
  margin: 0;
}
:host(.ws-tenant-halleckvineyard) .ws-rich-mem-price .ws-card-price-unit {
  font-size: 12px;
  color: #333333;
  margin: 0;
}

/* Carousel breathing room — Halleck cards have a 10px gold-glow shadow; give the
   track vertical padding + gap so adjacent white cards read as distinct tiles. */
:host(.ws-tenant-halleckvineyard) .ws-carousel-track {
  padding-top: 14px;
  padding-bottom: 14px;
  padding-left: 4px;
  padding-right: 4px;
  gap: 16px;
}
/* Carousel side gutters (so the nav arrows don't overlap card content) are now
   in the base — see .ws-carousel:not(:has(.ws-rich-review-button)) in ChatPanel.css. */

/* ── Event card — live createEvents WHITE card (gold glow): cover image on top,
   copper title + dark meta, footer with a learn-more pill and a price/CTA row.
   The Halleck EventCard component emits this white-card DOM (head + inner header
   + footer-row), matching the live .event_card. */
:host(.ws-tenant-halleckvineyard) .ws-rich-event {
  background: #ffffff;
  background-image: none;
  min-height: 0;
  box-shadow: 0 0 10px 0 rgba(189, 133, 39, 0.4);
  color: #1a1a1a;
}
:host(.ws-tenant-halleckvineyard) .ws-rich-event .ws-card-head {
  cursor: pointer;
}
:host(.ws-tenant-halleckvineyard) .ws-rich-event .ws-card-img {
  width: 100%;
  display: block;
  aspect-ratio: 16/9;
  object-fit: cover;
}
:host(.ws-tenant-halleckvineyard) .ws-rich-event-inner {
  position: static;
  inset: auto;
  background: transparent;
  -webkit-backdrop-filter: none;
  backdrop-filter: none;
  padding: 10px 12px 12px;
  flex: 1;
  display: flex;
  flex-direction: column;
}
:host(.ws-tenant-halleckvineyard) .ws-event-header {
  text-align: left;
}
:host(.ws-tenant-halleckvineyard) .ws-event-title {
  font-size: 16px;
  font-weight: 400;
  color: #a66836;
  margin: 0 0 8px;
}
:host(.ws-tenant-halleckvineyard) .ws-event-date {
  font-size: 14px;
  font-weight: 700;
  color: #000;
  margin: 0 0 2px;
}
:host(.ws-tenant-halleckvineyard) .ws-event-time,
:host(.ws-tenant-halleckvineyard) .ws-event-location {
  font-size: 12px;
  color: #574d39;
  margin: 0 0 2px;
}
:host(.ws-tenant-halleckvineyard) .ws-event-desc {
  font-size: 12px;
  color: #574d39;
  margin: 6px 0 0;
}
:host(.ws-tenant-halleckvineyard) .ws-rich-event .ws-event-footer {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: auto;
  align-items: stretch;
}
:host(.ws-tenant-halleckvineyard) .ws-event-footer-row {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 8px;
}
:host(.ws-tenant-halleckvineyard) .ws-event-price-block {
  display: flex;
  flex-direction: column;
}
:host(.ws-tenant-halleckvineyard) .ws-event-price-block .ws-card-price-amount {
  font-size: 16px;
  color: #a66836;
  margin: 0;
}
:host(.ws-tenant-halleckvineyard) .ws-event-price-block .ws-card-price-unit {
  font-size: 11px;
  color: #574d39;
  margin: 0;
}

/* ── Event details — live createEventDetails DECORATIVE card: grey card with the
   eventdetail.png pinned top-right, a white date pill, a #574D39 heading +
   divider + first paragraph, a #8D6E35 price row, and an #AD703E reserve button.
   The shared .ws-rich-event-details (cards.css) is the grey base; these override
   it for the decorative layout (relative + visible overflow for the pinned top). */
:host(.ws-tenant-halleckvineyard) .ws-rich-event-details {
  position: relative;
  max-width: 284px;
  overflow: visible;
  padding: 25px 10px 10px;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.08);
}
:host(.ws-tenant-halleckvineyard) .ws-rich-detail-top {
  position: absolute;
  top: 0;
  right: 10px;
  display: flex;
  justify-content: flex-end;
  max-width: 200px;
  margin-top: -25px;
}
:host(.ws-tenant-halleckvineyard) .ws-rich-detail-top-img {
  max-width: 200px;
  width: 100%;
}
:host(.ws-tenant-halleckvineyard) .ws-rich-detail-date {
  display: inline-block;
  border-radius: 48px;
  padding: 4px 10px;
  background: #ffffff;
  border: 1px solid rgba(192, 126, 50, 0.19);
  font-size: 13px;
  color: #574d39;
}
:host(.ws-tenant-halleckvineyard) .ws-rich-detail-heading {
  font-weight: 700;
  font-size: 16px;
  line-height: 100%;
  color: #574d39;
  width: 75%;
  margin: 20px 0 8px;
}
:host(.ws-tenant-halleckvineyard) .ws-rich-detail-divider {
  width: 149px;
  height: 1px;
  background: rgba(0, 0, 0, 0.12);
  margin: 8px 0;
}
:host(.ws-tenant-halleckvineyard) .ws-rich-detail-desc {
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  color: #574d39;
  width: 85%;
  margin: 0;
}
:host(.ws-tenant-halleckvineyard) .ws-rich-detail-price {
  display: flex;
  gap: 8px;
  align-items: center;
  margin: 20px 0;
  color: #8d6e35;
}
:host(.ws-tenant-halleckvineyard) .ws-rich-detail-price-detail {
  font-weight: 700;
  font-size: 14px;
  line-height: 16px;
  color: #574d39;
}
:host(.ws-tenant-halleckvineyard) .ws-rich-detail-amount {
  font-weight: 500;
}
:host(.ws-tenant-halleckvineyard) .ws-rich-detail-reserve {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 4px 12px 4px 6px;
  border-radius: 6px;
  background: #ad703e;
  color: #ffffff;
  text-decoration: none;
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  box-shadow: 0 2px 2px 0 rgba(162, 155, 141, 0.15);
  margin-bottom: 10px;
}

/* ── /vineyard-tours/ promo card (old .vineyard-tours-card) ───────────── */
:host(.ws-tenant-halleckvineyard) .ws-vineyard-tours-wrap {
  width: 100%;
  display: flex;
  justify-content: center;
  margin: 8px 0 12px;
}
:host(.ws-tenant-halleckvineyard) .ws-vineyard-tours-card {
  width: 100%;
  max-width: 630px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  margin: 0 auto;
}
:host(.ws-tenant-halleckvineyard) .ws-vineyard-tours-image {
  width: min(100%, 430px);
  max-height: 280px;
  height: auto;
  object-fit: contain;
  display: block;
}
:host(.ws-tenant-halleckvineyard) .ws-vineyard-tours-caption {
  margin: 0;
  color: #7f7f7f;
  font-size: 14px;
  line-height: 1.2;
  text-align: center;
  font-style: italic;
}
:host(.ws-tenant-halleckvineyard) .ws-vineyard-tours-learn-more {
  border: none;
  background: #ae733c;
  color: #fff;
  text-transform: uppercase;
  letter-spacing: 3px;
  font-size: 16px;
  line-height: 1;
  font-weight: 700;
  font-family: Lato, "Open Sans", serif, sans-serif;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 188px;
  white-space: nowrap;
  cursor: pointer;
  padding: 14px 32px;
  margin-top: 4px;
}
:host(.ws-tenant-halleckvineyard) .ws-vineyard-tours-learn-more:hover {
  background: #9a6433;
}

/* ── "Text a Live Person" RedChirp form — white card, copper Send button. */
:host(.ws-tenant-halleckvineyard) .ws-liveperson-form {
  background: #ffffff;
  border: 1px solid #f1f1f1;
  border-radius: 12px;
  padding: 16px 20px;
}
:host(.ws-tenant-halleckvineyard) .ws-liveperson-form .ws-checkout-field label {
  color: #1d1d1d;
  font-size: 15px;
  font-weight: 600;
}
:host(.ws-tenant-halleckvineyard) .ws-liveperson-form .ws-checkout-input {
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 10px 12px;
  color: #000000;
  font-size: 14px;
  height: auto;
}
:host(.ws-tenant-halleckvineyard) .ws-liveperson-form .ws-checkout-submit {
  background: #a66836;
  color: #ffffff;
  width: 100%;
  border: none;
  border-radius: 2px;
  padding: 10px 12px;
  font-size: 15px;
}

/* Reviews button row: common look + layout for every tenant now lives in the
   shared cards.css — Halleck no longer needs its own override. */

/* Tasting Notes Drawer */

:host(.ws-tenant-halleckvineyard) .ws-drawer-tab-collapse svg path,
:host(.ws-tenant-halleckvineyard) .ws-wine-drawer-header-icon svg path {
  stroke: var(--ws-primary);
}

:host(.ws-tenant-halleckvineyard) .ws-wine-drawer-header h2,
:host(.ws-tenant-halleckvineyard) .ws-wine-drawer-pane h4,
:host(.ws-tenant-halleckvineyard) .ws-wine-drawer-regular-price,
:host(.ws-tenant-halleckvineyard) .ws-drawer-fp-heading,
:host(.ws-tenant-halleckvineyard) .ws-drawer-ingredient-bullet,
:host(.ws-tenant-halleckvineyard) .ws-drawer-ingredient-amount {
  color: var(--ws-primary);
}

:host(.ws-tenant-halleckvineyard) .ws-wine-drawer-intro,
:host(.ws-tenant-halleckvineyard) .ws-drawer-pairing-box {
  background: #fffbedc2;
}

:host(.ws-tenant-halleckvineyard) .ws-wine-drawer-tab.active {
  background: var(--ws-primary);
  color: #fff;
  border-right: 5px solid var(--ws-primary);
}

:host(.ws-tenant-halleckvineyard) .ws-drawer-fp-card {
  border-top: 3px solid var(--ws-primary);
  background: #fffbedc2;
}
:host(.ws-tenant-halleckvineyard) .ws-drawer-method-num {
  background: var(--ws-primary);
}
